import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'storefront.recentlyViewed';
const MAX_ITEMS = 20;

const safeRead = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const safeWrite = (list) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
        /* quota / disabled storage — silent */
    }
};

/**
 * Local-storage backed list of recently viewed products. Items are
 * deduped by id (most recent moves to the front) and capped at
 * MAX_ITEMS. The hook returns the list + push/clear helpers.
 *
 * Server sync is intentionally not wired — the spec leaves it as a
 * stretch dependent on a backend endpoint that hasn't shipped.
 */
export const useRecentlyViewed = () => {
    const [items, setItems] = useState(() => safeRead());

    // Re-sync when other tabs mutate localStorage.
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === STORAGE_KEY) setItems(safeRead());
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const push = useCallback((product) => {
        if (!product?.id) return;
        // Strip down to a small payload — the consumer only needs basic
        // display fields. Pulling the full product into localStorage on
        // every PDP visit would balloon the entry quickly.
        const minimal = {
            id: product.id,
            slug: product.slug,
            name: product.name,
            price: product.price,
            currency: product.currency,
            thumb_url: product?.cover?.thumb_url || product.image,
        };
        setItems((prev) => {
            const filtered = prev.filter((p) => p.id !== product.id);
            const next = [minimal, ...filtered].slice(0, MAX_ITEMS);
            safeWrite(next);
            return next;
        });
    }, []);

    const clear = useCallback(() => {
        setItems([]);
        safeWrite([]);
    }, []);

    return { items, push, clear };
};

export default useRecentlyViewed;
